import React, { useEffect, useState } from "react";
import { Card } from "../../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { CommonTextField } from "../../components/widgets/common_textField";
import AuthService from "../../service/auth.service";
import { toastError, toastSuccess } from "../../common/toastNotification";
import CommonButton from "../../components/widgets/common_button";
import { Trash2, SquarePen } from "lucide-react";
import CommonModal from "../../components/widgets/CommonModal";
import Delete from "./Delete";

const OfferCode = () => {
  const [data, setData] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [newCode, setNewCode] = useState({ code: "", credit: "" });
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [loading, setLoading] = useState(false);

  const getOfferCode = async () => {
    try {
      const response = await AuthService.getOfferCode();
      setData(response?.data?.apiresponse?.data || []);
    } catch (error) {
      toastError(error || "Something went wrong");
    }
  };

  useEffect(() => {
    getOfferCode();
  }, []);

  const handleSaveOfferCode = async () => {
    try {
      setLoading(true);
      if (!newCode.code || !newCode.credit) {
        toastError("Please enter promo code and credit");
        return;
      }

      let response;
      if (isEditing) {
        response = await AuthService.updateOfferCode(
          editId,
          newCode.code,
          newCode.credit
        );
        if (response?.data?.status === "SUCCESS") {
          toastSuccess("Promo code updated!");
          setData((prev) =>
            prev.map((item) =>
              item.id === editId
                ? { ...item, promoCode: newCode.code, credit: newCode.credit }
                : item
            )
          );
        }
      } else {
        response = await AuthService.addOfferCode(newCode.code, newCode.credit);
        if (response?.data?.status === "SUCCESS") {
          toastSuccess("Promo code added!");
          const newItem = {
            id: response?.data?.apiresponse?.id || Date.now(),
            promoCode: newCode.code,
            credit: newCode.credit,
          };
          setData((prev) => [...prev, newItem]);
        }
      }

      await getOfferCode();
    } catch (error) {
      toastError(error?.message || "Failed to save promo code");
    } finally {
      // ✅ Always close modal and reset
      setLoading(false);
      setShowModal(false);
      setNewCode({ code: "", credit: "" });
      setIsEditing(false);
      setEditId(null);
    }
  };

  const handleEdit = (item) => {
    setNewCode({ code: item.promoCode, credit: item.credit });
    setEditId(item.id);
    setIsEditing(true);
    setShowModal(true);
  };

  // Open delete dialog
  const confirmDelete = (id) => {
    setDeleteId(id);
    setShowDeleteDialog(true);
  };

  // Delete handler
  const handleDelete = async () => {
    if (deleteId == null) return;

    // optimistic remove
    let previous = null;
    setData((prev) => {
      previous = Array.isArray(prev) ? prev.slice() : [];
      return prev.filter((item) => Number(item.id) !== Number(deleteId));
    });

    setShowDeleteDialog(false);
    const idToDelete = deleteId;
    setDeleteId(null);

    try {
      const response = await AuthService.deleteOfferCode(Number(idToDelete));
      console.debug("deleteOfferCode response:", response);

      // ✅ Treat 204 as success
      if (response?.status === 204 || response?.data?.status === "SUCCESS") {
        toastSuccess("Promo code deleted successfully!");
      } else {
        throw new Error(response?.data?.message || "Delete failed on server");
      }
    } catch (error) {
      // revert UI if failed
      setData(previous ?? []);
      toastError(error?.message || "Failed to delete promo code");
    }
  };

  return (
    <Card className="p-10 grid gap-6">
      <h3 className="text-xl tab:text-2xl font-bold">Offer Code</h3>

      <div className="flex justify-end">
        <CommonButton
          className="w-36"
          onClick={() => {
            setNewCode({ code: "", credit: "" });
            setIsEditing(false);
            setEditId(null);
            setShowModal(true);
          }}
        >
          <span className="text-base">Add New</span>
        </CommonButton>
      </div>

      {/* Table */}
      <Table className="whitespace-nowrap">
        <TableHeader>
          <TableRow>
            <TableHead className="w-14">No</TableHead>
            <TableHead>Offer Code</TableHead>
            {/* <TableHead>Credit</TableHead> */}
            <TableHead className="text-center w-10">Action</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data?.length > 0 ? (
            data?.map((item, index) => (
              <TableRow key={item.id || index}>
                <TableCell>{index + 1}</TableCell>
                <TableCell>{item.promoCode || "-"}</TableCell>
                {/* <TableCell>{item.credit || "-"}</TableCell> */}
                <TableCell>
                  <div className="flex items-center gap-2 justify-end">
                    <CommonButton
                      variant="outline"
                      className="size-9"
                      onClick={() => handleEdit(item)}
                    >
                      <SquarePen className="size-5" />
                    </CommonButton>
                    <CommonButton
                      variant="outline"
                      className="size-9"
                      onClick={() => confirmDelete(item.id)}
                    >
                      <Trash2 className="h-5 w-5 text-red-500" />
                    </CommonButton>
                  </div>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow className="h-40">
              <TableCell colSpan={8} className="text-center text-black/50">
                No Offer Codes Found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {/* Add/Edit Modal */}
      <CommonModal
        show={showModal}
        onClose={() => {
          setShowModal(false);
          setIsEditing(false);
          setEditId(null);
        }}
        title={isEditing ? "Edit Promo Credit" : "Add Promo Code"}
      >
        <div className="grid gap-4">
          <CommonTextField
            type="text"
            placeholder="Promo Code"
            value={newCode.code}
            onChange={(e) => setNewCode({ ...newCode, code: e.target.value })}
            readOnly={isEditing}
            className={isEditing ? "bg-gray-100 cursor-not-allowed" : ""}
          />
          <CommonTextField
            type="number"
            placeholder="Credit"
            value={newCode.credit}
            onChange={(e) => setNewCode({ ...newCode, credit: e.target.value })}
          />
          <div className="flex justify-end gap-2 mt-4">
            <CommonButton onClick={handleSaveOfferCode} disabled={loading}>
              {loading ? (
                <span className="flex items-center gap-2">
                  {isEditing ? "Updating..." : "Saving..."}
                </span>
              ) : isEditing ? (
                "Update"
              ) : (
                "Save"
              )}
            </CommonButton>
            <CommonButton
              variant="outline"
              onClick={() => {
                setShowModal(false);
                setIsEditing(false);
                setEditId(null);
              }}
            >
              Cancel
            </CommonButton>
          </div>
        </div>
      </CommonModal>

      {/* Delete Dialog */}
      <Delete
        isOpen={showDeleteDialog}
        setIsOpen={setShowDeleteDialog}
        handleDelete={handleDelete}
      />
    </Card>
  );
};

export default OfferCode;
